import { fetchGracenoteLineups, fetchWorkplaceById } from "core/API/GameMasterData";
import { fetchGracenoteChannels, fetchGracenoteTeams } from "core/API/gameMasterSports";
import Cookies from "js-cookie";
import { useQuery } from "react-query";
import { GracenoteTeam, WorkplaceDetails } from "types";
import { getCurrentWorkplaceId } from 'core/session/workplaceStore';

/**
 * Fetches Gracenote TV lineups for the given workplace.
 * Only runs when `workplaceId` is defined. Cached indefinitely.
 * @param workplaceId - The workplace ID to fetch lineups for.
 */
export const useLineups = (workplaceId: number | undefined) => {
    return useQuery({
        queryKey: ["gracenote-lineups", workplaceId],
        queryFn: () => fetchGracenoteLineups(workplaceId as number),
        staleTime: Infinity,
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!workplaceId,
    });
};

/**
 * Fetches full details for the current workplace, identified by the `workplace-id`
 * value in localStorage. Cached indefinitely after the first successful fetch.
 */
export const useWorkplace = () => {
    const workplaceId = getCurrentWorkplaceId()?.toString() ?? null;

    return useQuery<WorkplaceDetails>({
        queryKey: ["workplace", workplaceId],
        queryFn: () => fetchWorkplaceById(workplaceId!),
        enabled: !!workplaceId, // Only fetch if workplaceId exists
        staleTime: Infinity, // Fetch only once and cache forever
        retry: 1, // Retry once on failure
        refetchOnWindowFocus: false, // Do not refetch when window regains focus
        refetchOnReconnect: false, // Disable refetching on network reconnect
    });
};


/** Fetches all Gracenote sports teams. Cached indefinitely. */
export const useGracenoteTeams = () => {
    return useQuery<GracenoteTeam[]>({
        queryKey: ["gracenote-teams"],
        queryFn: fetchGracenoteTeams,
        staleTime: Infinity, // Data stays fresh forever
        retry: 1, // Retry once on failure
        refetchOnWindowFocus: false, // Disable refetching on window focus
    });
};



/**
 * Fetches Gracenote channels for a specific lineup.
 * Only runs when `lineupID` is non-empty. Cached indefinitely.
 * @param lineupID - The Gracenote lineup identifier.
 * @param lineupName - Optional lineup display name (used to detect DIRECTV).
 */
export const useChannels = (lineupID: string, lineupName?: string | null) => {
    return useQuery({
        queryKey: ["gracenote-channels", lineupID, lineupName ?? ""],
        queryFn: () => fetchGracenoteChannels(lineupID, lineupName),
        staleTime: Infinity,
        retry: 1,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!lineupID,
    });
};
